import React from 'react';
import { Reveal, Stagger } from '../ui';
import { formatInr } from './checkout';

/* ==================================================================
   PricingPlans
   ------------------------------------------------------------------
   The two product sections — Find Talent and Hire Through Jobs —
   rendered from the SERVER catalog. Nothing here hardcodes a price,
   a credit count or a validity; if this component renders a number,
   /api/billing/packages sent it.

   Used by both the standalone pricing page and the in-dashboard
   upgrade dialog, so a recruiter sees the same packages worded the
   same way wherever they hit the paywall.
   ================================================================== */

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}

// What each product actually gets you. Kept here rather than in the API
// because it is marketing copy, not commercial terms — the numbers that
// matter (price, credits, validity) all come from the server.
const PROFILE_FACTS = (pkg) => [
  `${pkg.credits} student profiles`,
  'Full contact details & resume',
  'LinkedIn, GitHub & portfolio links',
  'Direct messaging',
  `${pkg.validity_days}-day access`,
];

const JOB_FACTS = (pkg, jobValidityDays) => [
  `${pkg.credits} job post${pkg.credits > 1 ? 's' : ''}`,
  'Goes live immediately — no approval wait',
  'Emailed to matching students',
  'AI-recommended candidates per role',
  `Each job runs for ${jobValidityDays} days`,
];

function PlanCard({ pkg, facts, onSelect, disabled, ctaLabel }) {
  return (
    <div className={`bl-plan${pkg.highlight ? ' bl-plan--highlight' : ''}`}>
      {pkg.highlight && <span className="bl-plan-flag">Most popular</span>}

      <div className="bl-plan-name">{pkg.name}</div>

      <div className="bl-plan-price">
        <span className="bl-plan-amount">{formatInr(pkg.amount_paise)}</span>
        <span className="bl-plan-unit">one-time</span>
      </div>
      <p className="bl-plan-blurb">{pkg.blurb}</p>

      <ul className="bl-plan-facts">
        {facts.map((fact) => (
          <li key={fact} className="bl-plan-fact">
            <Check />
            {fact}
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={`bl-plan-cta${pkg.highlight ? '' : ' bl-plan-cta--ghost'}`}
        onClick={() => onSelect(pkg)}
        disabled={disabled}
        // The package name is in the label rather than only in the card, so a
        // screen reader announces which of three identical "Buy" buttons this is.
        aria-label={`${ctaLabel} — ${pkg.name}, ${formatInr(pkg.amount_paise)}`}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

export default function PricingPlans({
  packages,
  onSelect,
  disabled = false,
  products = ['profile', 'job'],
  compact = false,
}) {
  if (!packages) return null;

  const jobValidityDays = packages.job_validity_days || 30;
  const gstNote = packages.gst_note || 'Price is inclusive of all applicable taxes (GST).';

  const sections = [
    {
      key: 'profile',
      title: 'Find Talent',
      sub: 'Search verified students and unlock the ones you want to reach.',
      icon: <UsersIcon />,
      iconClass: '',
      list: packages.profile || [],
      facts: PROFILE_FACTS,
      cta: 'Get Profiles',
    },
    {
      key: 'job',
      title: 'Hire Through Jobs',
      sub: 'Post roles and let matching students come to you.',
      icon: <BriefcaseIcon />,
      iconClass: 'bl-section-icon--accent',
      list: packages.job || [],
      facts: (pkg) => JOB_FACTS(pkg, jobValidityDays),
      cta: 'Get Job Posts',
    },
  ].filter((s) => products.includes(s.key));

  return (
    <>
      {sections.map((section) => (
        <section className="bl-section" key={section.key}>
          <div className="bl-section-head">
            <div className="bl-section-title">
              <span className={`bl-section-icon ${section.iconClass}`.trim()} aria-hidden="true">
                {section.icon}
              </span>
              <div>
                <h2>{section.title}</h2>
                {!compact && <p className="bl-section-sub">{section.sub}</p>}
              </div>
            </div>
            {!compact && (
              <span className="bl-pill bl-pill--none" title={gstNote}>
                Incl. GST
              </span>
            )}
          </div>

          {/* Stagger drives the entrance off one observer, so nine cards cost
              one IntersectionObserver rather than nine. */}
          <Stagger className="bl-plans">
            {section.list.map((pkg) => (
              <PlanCard
                key={pkg.key}
                pkg={pkg}
                facts={section.facts(pkg)}
                onSelect={onSelect}
                disabled={disabled}
                ctaLabel={section.cta}
              />
            ))}
          </Stagger>
        </section>
      ))}

      {!compact && (
        <Reveal variant="fade">
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            {gstNote} Payments are processed securely by Razorpay — HyrSkill never
            sees or stores your card details.
          </p>
        </Reveal>
      )}
    </>
  );
}
